import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CHeader, CLoader, CButton } from '../../components/common';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { fetchExpertAvailability, TimeSlot } from '../../api';

const ExpertBooking: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const bookingType = searchParams.get('type') || 'call'; // 'call' or 'chat'

  const colors = useAppSelector((state) => state.theme.theme);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  useEffect(() => {
    loadAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, id]);

  const loadAvailability = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const data = await fetchExpertAvailability(parseInt(id), dateStr);
      setSlots(data.slots || []);
      setSelectedSlot(null);
    } catch (error) {
      console.error('Error fetching availability:', error);
      setSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatSlotRange = (isoString: string) => {
    const start = new Date(isoString);
    const end = new Date(start.getTime() + 30 * 60 * 1000);
    return `${formatTime(isoString)} - ${formatTime(end.toISOString())}`;
  };

  const isSlotPast = (slotTime: string) => {
    return new Date(slotTime) < new Date();
  };

  const handleProceed = () => {
    if (!selectedSlot) return;

    const selectedSlotDate = new Date(selectedSlot);
    const selectedSlotEndDate = new Date(selectedSlotDate.getTime() + 30 * 60 * 1000);

    navigate(`/expert/${id}/book/confirm`, {
      state: {
        expertId: id,
        selectedSlotDate: selectedSlotDate.toISOString(),
        selectedSlotEndDate: selectedSlotEndDate.toISOString(),
        bookingType: bookingType === 'chat' ? 'C' : 'V',
      },
    });
  };

  // Calendar helpers
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth };
  };

  const { firstDay, daysInMonth } = getDaysInMonth(currentMonth);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (day: number) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (newDate >= today) {
      setSelectedDate(newDate);
    }
  };

  const isDateSelected = (day: number) => {
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === currentMonth.getMonth() &&
      selectedDate.getFullYear() === currentMonth.getFullYear()
    );
  };

  const isDatePast = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  return (
    <div
      className="p-4 min-h-screen pb-24"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      {loading && <CLoader />}

      <CHeader title="Book Appointment" />

      {/* Booking Type Badge */}
      <div className="mb-4">
        <span
          className="px-3 py-1 rounded-full text-sm"
          style={{
            backgroundColor: colors.primary + '20',
            color: colors.primary,
          }}
        >
          {bookingType === 'chat' ? '30 min Chat Session' : '30 min Video Call'}
        </span>
      </div>

      {/* Calendar */}
      <div
        className="rounded-xl p-4 mb-4"
        style={{ backgroundColor: colors.inputBackground }}
      >
        {/* Month Navigation */}
        <div className="flex justify-between items-center mb-4">
          <button onClick={handlePrevMonth} className="p-2">
            {React.createElement(FiChevronLeft as any, { size: 20, color: colors.textColor })}
          </button>
          <CText type="B16" color={colors.textColor}>
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </CText>
          <button onClick={handleNextMonth} className="p-2">
            {React.createElement(FiChevronRight as any, { size: 20, color: colors.textColor })}
          </button>
        </div>

        {/* Day Names */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map((day) => (
            <div key={day} className="text-center">
              <CText type="r12" color={colors.grayScale500}>
                {day}
              </CText>
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for days before first day */}
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`empty-${i}`} className="h-10" />
          ))}

          {/* Days */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const isPast = isDatePast(day);
            const isSelected = isDateSelected(day);

            return (
              <button
                key={day}
                className="h-10 rounded-lg flex items-center justify-center transition-colors"
                style={{
                  backgroundColor: isSelected ? colors.primary : 'transparent',
                  opacity: isPast ? 0.3 : 1,
                  cursor: isPast ? 'not-allowed' : 'pointer',
                }}
                onClick={() => !isPast && handleDateClick(day)}
                disabled={isPast}
              >
                <CText
                  type={isSelected ? 'M14' : 'r14'}
                  color={isSelected ? colors.white : colors.textColor}
                >
                  {day}
                </CText>
              </button>
            );
          })}
        </div>
      </div>

      {/* Available Slots */}
      <div className="mb-4">
        <CText type="B16" color={colors.textColor} style={{ marginBottom: 12 }}>
          Available Slots on {selectedDate.toLocaleDateString()}
        </CText>

        {slots.length === 0 ? (
          <CText type="r14" color={colors.grayScale500} className="text-center py-4">
            No slots available for this date
          </CText>
        ) : (
          <div className="flex flex-wrap gap-2">
            {slots.map((slot) => {
              const available = slot.is_available;
              const past = isSlotPast(slot.time);
              const selected = selectedSlot === slot.time;

              return (
                <button
                  key={slot.time}
                  className="px-3 py-2 rounded-lg text-sm transition-colors"
                  style={{
                    backgroundColor: selected
                      ? colors.primary
                      : available
                      ? colors.inputBackground
                      : '#333',
                    color: selected
                      ? colors.white
                      : available
                      ? colors.textColor
                      : colors.grayScale500,
                    opacity: past || !available ? 0.5 : 1,
                    cursor: available && !past ? 'pointer' : 'not-allowed',
                    border: `1px solid ${available ? colors.borderColor : 'transparent'}`,
                  }}
                  onClick={() => available && !past && setSelectedSlot(slot.time)}
                  disabled={!available || past}
                >
                  {formatSlotRange(slot.time)}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Proceed Button */}
      {selectedSlot && (
        <div className="mt-6">
          <CButton title="Proceed" onPress={handleProceed} />
        </div>
      )}
    </div>
  );
};

export default ExpertBooking;
